<script setup lang="ts">
import { usePrice } from '@/composables/price'
import { useTracker } from '@/composables/tracker'
import useProductStore from '@/stores/product'
import { useClipboard } from '@vueuse/core'
import { AppBlock, AppButton, useDialog, useToast } from 'lilasia-ui'
import { useI18n } from 'vue-i18n'
import AppImage from '../common/AppImage.vue'
import CloseButton from '../common/CloseButton.vue'

const { trackShare } = useTracker()
const { formatMoney } = usePrice()
const { t } = useI18n()
const { hideDialog } = useDialog()
const { toastSuccess, toastError } = useToast()
const { copy } = useClipboard({ legacy: true })
const productStore = useProductStore()

const copyLink = async () => {
  try {
    await copy(window.location.href)
    toastSuccess('Link Copied')
    if (productStore.selectedProduct) {
      await trackShare(productStore.selectedProduct)
    }
    hideDialog()
  } catch {
    toastError('Failed to copy the link')
  }
}
</script>

<template>
  <AppBlock class="w-full sm:w-[432px]">
    <div
      v-if="productStore.selectedProduct"
      class="px-24 py-16"
    >
      <div class="flex flex-col gap-16">
        <div class="flex items-center justify-between gap-16">
          <div class="text-20 font-600 leading-28">
            {{ t('share_this_product') }}
          </div>
          <CloseButton @click="hideDialog" />
        </div>

        <div class="text-14 font-500 leading-20">
          Copy the link below and share it on your favorite app to spread the word!
        </div>

        <div class="relative">
          <AppImage
            :src="String(productStore.selectedProduct.image_url)"
            :alt="productStore.selectedProduct.name"
            :width="600"
            class="w-[600px] rounded-8 border border-black-10 object-cover"
          />
          <div class="absolute inset-[0] rounded-8 bg-gradient-to-t from-black/60"></div>
          <div class="absolute inset-x-[0] bottom-16 px-16">
            <div class="flex flex-col gap-8 text-white">
              <div class="line-clamp-3 text-16 leading-24">
                {{ productStore.selectedProduct.name }}
              </div>
              <div class="text-24 font-700">
                {{ formatMoney(productStore.selectedProduct.price) }}
              </div>
            </div>
          </div>
        </div>

        <AppButton
          icon="link"
          :text="t('copy_link')"
          block
          @click="copyLink"
        />
      </div>
    </div>
  </AppBlock>
</template>
